import { useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFieldMutations } from "@/hooks/use-form-mutations";
import { defaultProperties } from "@/schemas/field-schemas";
import type { FieldType, FieldProperties } from "@/types/forms";

const typeLabels: Record<string, string> = {
  short_text: "Short Text",
  long_text: "Long Text",
  rich_text: "Rich Text",
  number: "Number",
  slider: "Slider",
  rating: "Rating",
  dropdown: "Dropdown",
  checkbox: "Checkbox",
  yes_no: "Yes / No",
  emoji_scale: "Emoji Scale",
  date: "Date",
  time: "Time",
  datetime: "Date & Time",
};

interface NewFieldEditorProps {
  type: FieldType;
  formId: string;
  onClose: () => void;
}

export function NewFieldEditor({ type, formId, onClose }: NewFieldEditorProps) {
  const { addField } = useFieldMutations(formId);
  const [label, setLabel] = useState("");
  const [required, setRequired] = useState(false);
  const [properties, setProperties] = useState<FieldProperties>(
    () => (defaultProperties[type] as FieldProperties) ?? {}
  );

  async function handleAdd() {
    if (!label.trim()) {
      toast.error("Label is required");
      return;
    }
    try {
      await addField.mutateAsync({
        type,
        label,
        required,
        properties:
          Object.keys(properties).length > 0
            ? JSON.stringify(properties)
            : undefined,
      });
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add field");
    }
  }

  return (
    <div className="rounded-lg border-2 border-primary/20 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">New Field</p>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
            {typeLabels[type] ?? type}
          </Badge>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Label</Label>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Question text"
          autoFocus
        />
      </div>

      <TypeSpecificConfig
        type={type}
        properties={properties}
        onChange={setProperties}
      />

      <div className="flex items-center justify-between">
        <Label>Required</Label>
        <Switch checked={required} onCheckedChange={setRequired} />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleAdd} disabled={addField.isPending}>
          {addField.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Add Field
        </Button>
      </div>
    </div>
  );
}

function TypeSpecificConfig({
  type,
  properties,
  onChange,
}: {
  type: FieldType;
  properties: FieldProperties;
  onChange: (props: FieldProperties) => void;
}) {
  function set<K extends keyof FieldProperties>(
    key: K,
    value: FieldProperties[K]
  ) {
    onChange({ ...properties, [key]: value });
  }

  switch (type) {
    case "short_text":
    case "long_text":
    case "rich_text":
    case "number":
    case "date":
    case "time":
    case "datetime":
      return (
        <div className="space-y-2">
          <Label>Placeholder</Label>
          <Input
            value={properties.placeholder ?? ""}
            onChange={(e) => set("placeholder", e.target.value)}
            placeholder="Placeholder text"
          />
        </div>
      );

    case "slider":
      return (
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Min</Label>
            <Input
              type="number"
              value={properties.min ?? 0}
              onChange={(e) => set("min", Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max</Label>
            <Input
              type="number"
              value={properties.max ?? 100}
              onChange={(e) => set("max", Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Step</Label>
            <Input
              type="number"
              value={properties.step ?? 1}
              onChange={(e) => set("step", Number(e.target.value))}
            />
          </div>
        </div>
      );

    case "rating":
      return (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Max Rating</Label>
            <Select
              value={String(properties.max ?? 5)}
              onValueChange={(v) => set("max", Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Icon</Label>
            <Select
              value={properties.icon ?? "star"}
              onValueChange={(v) =>
                set("icon", v as "star" | "heart" | "circle")
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="star">Star</SelectItem>
                <SelectItem value="heart">Heart</SelectItem>
                <SelectItem value="circle">Circle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case "dropdown":
    case "checkbox":
      return (
        <OptionsEditor
          options={properties.options ?? []}
          onChange={(opts) => onChange({ ...properties, options: opts })}
        />
      );

    case "yes_no":
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">True Label</Label>
            <Input
              value={properties.trueLabel ?? "Yes"}
              onChange={(e) => set("trueLabel", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">False Label</Label>
            <Input
              value={properties.falseLabel ?? "No"}
              onChange={(e) => set("falseLabel", e.target.value)}
            />
          </div>
        </div>
      );

    case "emoji_scale":
      return (
        <EmojiOptionsEditor
          emojis={properties.emojis ?? []}
          onChange={(emojis) => onChange({ ...properties, emojis })}
        />
      );

    default:
      return null;
  }
}

function OptionsEditor({
  options,
  onChange,
}: {
  options: { label: string; value: string }[];
  onChange: (opts: { label: string; value: string }[]) => void;
}) {
  function updateOption(index: number, key: "label" | "value", val: string) {
    const next = [...options];
    next[index] = { ...next[index], [key]: val };
    onChange(next);
  }
  function addOption() {
    const n = options.length + 1;
    onChange([...options, { label: `Option ${n}`, value: `option_${n}` }]);
  }
  function removeOption(index: number) {
    onChange(options.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      <Label>Options</Label>
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={opt.label}
            onChange={(e) => updateOption(i, "label", e.target.value)}
            placeholder="Label"
            className="flex-1"
          />
          <Input
            value={opt.value}
            onChange={(e) => updateOption(i, "value", e.target.value)}
            placeholder="Value"
            className="w-28"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive shrink-0"
            onClick={() => removeOption(i)}
            disabled={options.length <= 1}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addOption}>
        <Plus className="mr-2 h-4 w-4" />
        Add Option
      </Button>
    </div>
  );
}

function EmojiOptionsEditor({
  emojis,
  onChange,
}: {
  emojis: { emoji: string; label: string; value: string }[];
  onChange: (emojis: { emoji: string; label: string; value: string }[]) => void;
}) {
  function updateEmoji(
    index: number,
    key: "emoji" | "label" | "value",
    val: string
  ) {
    const next = [...emojis];
    next[index] = { ...next[index], [key]: val };
    onChange(next);
  }
  function addEmoji() {
    const n = emojis.length + 1;
    onChange([
      ...emojis,
      { emoji: "😀", label: `Option ${n}`, value: String(n) },
    ]);
  }
  function removeEmoji(index: number) {
    onChange(emojis.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      <Label>Emojis</Label>
      {emojis.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={item.emoji}
            onChange={(e) => updateEmoji(i, "emoji", e.target.value)}
            className="w-14 text-center"
          />
          <Input
            value={item.label}
            onChange={(e) => updateEmoji(i, "label", e.target.value)}
            placeholder="Label"
            className="flex-1"
          />
          <Input
            value={item.value}
            onChange={(e) => updateEmoji(i, "value", e.target.value)}
            placeholder="Value"
            className="w-16"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive shrink-0"
            onClick={() => removeEmoji(i)}
            disabled={emojis.length <= 1}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addEmoji}>
        <Plus className="mr-2 h-4 w-4" />
        Add Emoji
      </Button>
    </div>
  );
}
